/**
 * @module Model.ProgramModel
 *
 */
import { Model } from "typedefs";
import { StudentProfileModel } from "./StudentProfileModel";
import { StudentProgramRegistrationModel } from "./StudentProgramRegistrationModel";
import { CoachProfileModel } from "./CoachProfileModel";
import { DateTime } from "luxon";

interface StudentProgramRegistration {
  id: number;
  studentProfileId: string;
  status: string;
  createdAt: string;
  canRemove: boolean;
  paymentMethod: 'offline' | 'online';
  paymentStatus: 'pending_payment' | 'payment_confirmed' | 'payment_expired' | null;
  displayStatus: string | null;
  expiresAt?: string;
}

export class ProgramModel implements Model {
  id: number;
  title: string;
  subtitle: string;
  category: string;
  location: string;
  email: string;
  phone: string;
  shortDescription: string;
  longDescription: string;
  capacity: number;
  withWaitlist: boolean;
  spotsBooked: number;
  status: string;
  registrationStatus: string;
  registrationStatusCode: string;
  facilityId: string;
  facilityCurrency: string;
  facilityCurrencySymbol: string;
  facilityName: string;
  timeZone: string;
  startTime: string;
  endTime: string;
  price: number;
  priceCents: number;
  priceCurrency: string;
  isFree: boolean;
  externalThumbnailUrl: string;
  externalHeaderUrl: string;
  flyerPdfUrl: string;
  hasRegistrations: boolean;
  hasPendingRegistrations: boolean;
  canEditRegistrationSettings: boolean;
  canEditPaymentCollector: boolean;
  canEditPaymentMethod: boolean;
  coaches: CoachProfileModel[];
  collectType: string;
  onlineDefault: boolean;
  paymentCollectorCoachProfileId: string | null;
  allowAdditionalPeople: boolean;
  startDate: DateTime;
  tags: { id: number; name: string }[];
  start: string;

  private studentProfiles: StudentProfileModel[];
  private studentProgramRegistrations: StudentProgramRegistration[];

  constructor(program: any) {
    this.id = program.id;
    this.title = program.title;
    this.subtitle = program.subtitle;
    this.category = program.category;
    this.location = program.location;
    this.email = program.email;
    this.phone = program.phone;
    this.shortDescription = program.shortDescription;
    this.longDescription = program.longDescription;
    this.capacity = program.capacity;
    this.withWaitlist = program.withWaitlist;
    this.spotsBooked = program.spotsBooked;
    this.status = program.status;
    this.registrationStatus = program.registrationStatus;
    this.registrationStatusCode = program.registrationStatusCode;
    this.facilityId = program.facilityId;
    this.facilityCurrency = program.facilityCurrency;
    this.facilityCurrencySymbol = program.facilityCurrencySymbol;
    this.facilityName = program.facilityName;
    this.timeZone = program.timeZone;
    this.startTime = program.startTime;
    this.endTime = program.endTime;
    this.price = program.price;
    this.priceCents = program.priceCents;
    this.priceCurrency = program.priceCurrency;
    this.isFree = program.free ?? program.isFree ?? false;
    this.externalThumbnailUrl = program.externalThumbnailUrl;
    this.externalHeaderUrl = program.externalHeaderUrl;
    this.flyerPdfUrl = program.flyerPdfUrl;
    this.hasRegistrations = program.hasRegistrations;
    this.hasPendingRegistrations = program.hasPendingRegistrations;
    this.canEditRegistrationSettings = program.canEditRegistrationSettings ?? true;
    this.canEditPaymentCollector = program.canEditPaymentCollector ?? true;
    this.canEditPaymentMethod = program.canEditPaymentMethod ?? true;
    this.collectType = program.collectType || 'offline';
    this.onlineDefault = program.onlineDefault || false;
    this.paymentCollectorCoachProfileId = program.paymentCollectorCoachProfileId || null;
    this.allowAdditionalPeople = program.restrictAdditionalPeople == null ? program.allowAdditionalPeople : !program.restrictAdditionalPeople;
    this.startDate = program.startDate;
    this.tags = program.tags;
    this.start = program.start;

    // Parse coaches
    this.coaches = [];
    if (program.coaches && program.coaches.length > 0) {
      this.coaches = program.coaches.map(
        (coach: any) => new CoachProfileModel(coach)
      );
    }

    // Parse student profiles
    this.studentProfiles = [];
    if (program.studentProfiles && program.studentProfiles.length > 0) {
      this.studentProfiles = program.studentProfiles.map(
        (profile: any) => new StudentProfileModel(profile)
      );
    }

    // Store registrations
    this.studentProgramRegistrations = program.studentProgramRegistrations || [];
  }

  /**
   * Get list of completed registrants (includes pending payments)
   */
  get completedRegistrants(): StudentProgramRegistrationModel[] {
    return this.studentProgramRegistrations
      .filter(reg => reg.status === "completed" || reg.status === 'pending')
      .map(reg => {
        const student = this.studentProfiles.find(sp => sp.id === reg.studentProfileId);
        if (student) {
          return new StudentProgramRegistrationModel(reg, student);
        }
        return null;
      })
      .filter(Boolean) as StudentProgramRegistrationModel[];
  }

  /**
   * Get list of fully completed registrants (excludes pending payments)
   */
  get fullyCompletedRegistrants(): StudentProgramRegistrationModel[] {
    return this.studentProgramRegistrations
      .filter(reg => reg.status === "completed")
      .map(reg => {
        const student = this.studentProfiles.find(sp => sp.id === reg.studentProfileId);
        if (student) {
          return new StudentProgramRegistrationModel(reg, student);
        }
        return null;
      })
      .filter(Boolean) as StudentProgramRegistrationModel[];
  }

  get waitlistedRegistrants(): StudentProgramRegistrationModel[] {
    return this.studentProgramRegistrations
      .filter(reg => reg.status === "on_waitlist")
      .map(reg => {
        const student = this.studentProfiles.find(sp => sp.id === reg.studentProfileId);
        if (student) {
          return new StudentProgramRegistrationModel(reg, student);
        }
        return null;
      })
      .filter(Boolean) as StudentProgramRegistrationModel[];
  }

  /**
   * Get all registrants (completed + waitlisted)
   */
  get allRegistrants(): StudentProgramRegistrationModel[] {
    return [...this.completedRegistrants, ...this.waitlistedRegistrants];
  }

  /**
   * Get number of spots remaining
   */
  get spotsRemaining(): number {
    return Math.max(0, this.capacity - this.spotsBooked);
  }
}
